import { UserType } from "./types/userType";
import { generateAuthorizer, enumifyList } from "@coherehealth/common";
import useUserRoles from "./hooks/useUserRoles";

/**
 * Roles granted access to each feature type
 */
export const Permissions = enumifyList([
  "QM_PILOT",
  "QM_USER",
  "SUPERVISOR",
  "QM_MANAGER",
  "PRODUCTIVITY_REPORTS",
  "AUDIT_REVIEW",
] as const);

type Feature = keyof typeof Permissions;

export function useAuthorized(feature: Feature): boolean {
  const userRoles = useUserRoles();

  if (userRoles === undefined || userRoles.length === 0) {
    return false;
  }

  return isAuthorized(feature, userRoles);
}

const PERMISSION_HIERARCHY = Object.freeze({
  [UserType.Admin]: [
    UserType.QMPilot,
    UserType.ServiceOps,
    UserType.QMManager,
    UserType.ReportViewer,
    UserType.CohereEngOnCall,
    UserType.CoherePm,
    UserType.AuditReviewer,
  ],
  [UserType.QMPilot]: [Permissions.QM_PILOT],
  [UserType.ServiceOps]: [UserType.ServiceOpsGeneral],
  [UserType.ServiceOpsGeneral]: [Permissions.QM_USER],
  [UserType.QMManager]: [UserType.Assigner, Permissions.QM_MANAGER],
  [UserType.Assigner]: [Permissions.QM_USER, Permissions.SUPERVISOR],
  [UserType.ReportViewer]: [Permissions.PRODUCTIVITY_REPORTS],
  [UserType.CohereEngOnCall]: [
    Permissions.QM_MANAGER,
    Permissions.QM_PILOT,
    Permissions.QM_USER,
    Permissions.SUPERVISOR,
  ],
  [UserType.CoherePm]: [Permissions.QM_MANAGER, Permissions.QM_PILOT, Permissions.QM_USER, Permissions.SUPERVISOR],
  [UserType.NurseReviewer]: [Permissions.QM_USER],
  [UserType.MDReviewer]: [Permissions.QM_USER],
  [UserType.AuditReviewer]: [Permissions.AUDIT_REVIEW],
});

const isAuthorized = generateAuthorizer<Feature>(PERMISSION_HIERARCHY);
